// views/Dashboard/PainelExecutivo/sections/equipes/exportarEquipes.ts
//
// Excel da área "Equipes": uma aba por equipe (as colunas mudam de uma função
// para outra, então não cabem numa aba só) e uma aba "Como ler" com o que cada
// indicador mede. Os valores saem das mesmas funções da tela.
import { buildSheet, downloadWorkbook, fileStamp, type ColType } from '../../../../../src/lib/excelExport';
import {
  valorDaEquipe,
  valorDoIndicador,
  type Indicador,
  type UnidadeBi,
} from '../../../../../src/lib/dashboard/eficienciaEquipes';

const SIGLA: Record<string, string> = { Goiânia: 'GYN', Morrinhos: 'MOR' };

/** Contagem inteira vai como número; razão, média e medida com casas, com duas. */
const tipoExcel = (c: Indicador): ColType => {
  if (c.formato === 'pct') return 'pct';
  if (c.formato === 'brl') return 'brl';
  if (c.formato === 'num' && c.calculo.tipo === 'bruto' && c.calculo.equipe === 'soma') return 'num';
  return 'kwh';
};

/** O formato 'pct' do Excel só acrescenta o "%": o número precisa ir multiplicado. */
const paraCelula = (c: Indicador, valor: number | null): string | number => {
  if (valor === null || !Number.isFinite(valor)) return '';
  return c.formato === 'pct' ? valor * 100 : valor;
};

/** Nome de aba: até 31 caracteres e sem os símbolos que o Excel recusa. */
const nomeDaAba = (texto: string) => texto.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31);

export function exportarEquipes(unidades: UnidadeBi[], rotuloPeriodo: string): void {
  const abas: { name: string; ws: ReturnType<typeof buildSheet> }[] = [];
  const usados = new Map<string, Indicador>();

  for (const u of unidades) {
    for (const e of u.equipes) {
      e.colunas.forEach((c) => usados.set(c.id, c));
      const headers = ['Pessoa', ...e.colunas.map((c) => c.rotulo)];
      const tipos: ColType[] = ['txt', ...e.colunas.map(tipoExcel)];
      const linhas = e.pessoas.map((l) => [
        l.pessoa.nome,
        ...e.colunas.map((c) => paraCelula(c, valorDoIndicador(c, l.valores))),
      ]);
      const membros = e.pessoas.map((l) => l.valores);
      const totais = e.pessoas.length > 1
        ? ['Equipe', ...e.colunas.map((c) => paraCelula(c, valorDaEquipe(c, membros)))]
        : undefined;

      abas.push({
        name: nomeDaAba(`${SIGLA[u.unidade] ?? u.unidade} ${e.departamento}`),
        ws: buildSheet(headers, linhas, tipos, {
          totals: totais,
          widths: [30, ...e.colunas.map((c) => Math.max(14, c.rotulo.length + 2))],
        }),
      });
    }
  }

  if (abas.length === 0) throw new Error('Não há equipe para exportar.');

  const comoLer = [
    ['Período', rotuloPeriodo],
    ['Linha "Equipe"', 'Soma da equipe; em razões (por dia, %, tempo médio) é a soma ÷ soma; em dias ativos é a média por pessoa.'],
    ...Array.from(usados.values()).map((c) => [c.rotulo, c.explicacao]),
  ];
  abas.push({ name: 'Como ler', ws: buildSheet(['Indicador', 'O que mede'], comoLer, ['txt', 'txt'], { widths: [28, 110] }) });

  downloadWorkbook(abas, `bi-equipes_${fileStamp()}.xlsx`);
}
